import { Dialog } from "@headlessui/react";
import { FormBox, ModalFoterBox } from "../../components/box/Box";
import Button from "../../components/button/Button";
import { Modal, ModalProp } from "../../components/modal/Modal";
import { Controller, useForm } from "react-hook-form";
import { VideoContents, videoContentsSchema } from "../../model/VideoContents";
import { yupResolver } from "@hookform/resolvers/yup";
import { useEffect, useRef, useState } from "react";
import FormController from "../../components/fields/FormController";
import { Label, TextField } from "../../components/fields/Fields";
import { RadioButton } from "../../components/radiobutton/RadioButton";
import { useInput } from "../../hooks/useInput";
import companiesQuery from "../../firestore/Companies.query";
import { auth } from "../../firebase";
import MoviePointSettingModal, { PointForm } from "./MoviePointSettingModal";
import { checkOption, isOptionActive } from "../../controller/OptionController";
import { useDispatch } from "react-redux";
import { alertOpen, AlertValue } from "../../features/alert.slice";

export type MovieContentsAddModalProp = {
  onContentsAddSubmit: (data: VideoContents) => Promise<void>;
  data?: VideoContents;
  isLoad: boolean;
}

export const MovieContentsAddModal = ({
  open,
  toggle,
  closeOnClickOutside,
  onContentsAddSubmit: onContentsAdd,
  data,
  isLoad
}: ModalProp & MovieContentsAddModalProp) => {
  const dispatch = useDispatch();
  const { control, handleSubmit, watch, getValues, setValue, reset } =
    useForm<VideoContents>({
      resolver: yupResolver(videoContentsSchema),
      defaultValues: data
    });

  const selectTypeItems = [
    {id: "youtube", title: "YouTube", value: "YouTube"},
    {id: "url", title: "URL", value: "URL"}
  ];
  const selectType = useInput("YouTube");

  // ログイン中のユーザー情報
  const currentUser = auth.currentUser;

  const [optionFlg, setOptionFlg] = useState<{ point: boolean } | null>(null);

  async function fetchOptions() {
    const options = await companiesQuery.getOptionContractList(currentUser?.tenantId!);
    // ポイント管理のステータス、期間チェック
    const pointOption = options.find((option) => option.optionId === 'point');
    const point = pointOption ? isOptionActive(pointOption) : false;  
    return { point };
  }

  useEffect(() => {
    async function loadOptions() {
      const result = await fetchOptions();
      setOptionFlg(result);
    }
  
    loadOptions();
  }, []);

  // ポイント設定押下
  const [isMoviePointSettingModalOpen, setMoviePointSettingModalOpen] = useState(false);
  const pointModalRef = useRef<{ resetForm: () => void }>(null);
  const openMoviePointSettingModal = () => setMoviePointSettingModalOpen(true);
  const closeMoviePointSettingModal = () => setMoviePointSettingModalOpen(false);
  const modalContentRef = useRef<HTMLDivElement>(null);

  const handlePointSettingSubmit = (data: PointForm) => {
    setValue('pointSettingFlg', true);
    setValue('pointId', data.id);
    setValue('pointTypeId', data.typeId);
    setValue('pointTypeName', data.typeName);
    setValue('pointName', data.pointName);
    setValue('pointAssignmentPoint', data.point);
    setValue('pointCategoryId', data.categoryId);
    setValue('pointCategoryName', data.categoryName);
    closeMoviePointSettingModal();
  };

  const handlePointDelete = () => {
    setValue('pointSettingFlg', false);
    setValue('pointId', "");
    setValue('pointTypeId', "");
    setValue('pointTypeName', "");
    setValue('pointName', "");
    setValue('pointAssignmentPoint', 0);
    setValue('pointCategoryId', 0);
    setValue('pointCategoryName', "");
    if (pointModalRef.current) {
      pointModalRef.current.resetForm();
    }
  };
  
  const pointSettingFlg = watch('pointSettingFlg');
  useEffect(() => {
    if (isMoviePointSettingModalOpen && modalContentRef.current) {
      modalContentRef.current.focus();
    }
  }, [isMoviePointSettingModalOpen]);

  useEffect(() => {
    if (open && data) {
      setValue('id', data.id ?? '');
      setValue('title', data.title ?? '');
      setValue('url', data.url ?? '');
      setValue('categoryIdOrderMaps', data.categoryIdOrderMaps ?? {});
      setValue('explanation', data.explanation ?? '');
      selectType.setValue(data.type ?? "YouTube");
      setValue('pointSettingFlg', data.pointSettingFlg ?? false);
      setValue('pointId', data.pointId ?? '');
      setValue('pointTypeId', data.pointTypeId ?? '');
      setValue('pointTypeName', data.pointTypeName ?? '');
      setValue('pointName', data.pointName ?? '');
      setValue('pointAssignmentPoint', data.pointAssignmentPoint ?? 0);
      setValue('pointCategoryId', data.pointCategoryId ?? 0);
      setValue('pointCategoryName', data.pointCategoryName ?? '');
    }
  }, [open]);

  useEffect(() => {
    setValue('type', selectType.value);
  },[selectType.value, setValue])

  useEffect(() => {
    if(!open) {
      reset();
      selectType.setValue(data?.type);
    };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  },[data?.type, open, reset]);

  useEffect(() => {
    if (optionFlg !== null && !optionFlg.point) {
      setValue('pointSettingFlg', false);
      setValue('pointId', "");
      setValue('pointTypeId', "");
      setValue('pointTypeName', "");
      setValue('pointName', "");
      setValue('pointAssignmentPoint', 0);
      setValue('pointCategoryId', 0);
      setValue('pointCategoryName', "");
    }
  }, [optionFlg]);

  const onValidSubmit = async (formData: VideoContents) => {
    if (formData.pointSettingFlg) {
      const pointEnabled = await checkOption("point", dispatch, false);
      if (!pointEnabled) {
        dispatch(
          alertOpen(
            AlertValue(
              "ポイント管理のオプションが無効のため、ポイントを設定できません。",
              "danger"
            )
          )
        );
        return;
      }
    }
    await onContentsAdd(formData);
  };

  return (
    <>
      <Modal
        open={open}
        toggle={toggle}
        closeOnClickOutside={closeOnClickOutside}
      >
        <Dialog.Panel className=" relative transform overflow-hidden rounded-lg bg-white  text-left shadow-xl transition-all sm:w-full max-w-screen-md ">
          <form onSubmit={handleSubmit(onValidSubmit)}>
            <div className="text-center sm:mt-3">
              <Dialog.Title
                as="h3"
                className="text-left p-4 text-lg font-medium leading-6 text-gray-900"
              >
                動画コンテンツ登録
              </Dialog.Title>
              <div className="overflow-y-auto">
                <div className="text-left max-h-[calc(100vh_-_16rem)] p-4 ">
                  <FormBox gridcol={"grid-cols-2"} className={"sm:pt-[0px]"}>
                    <FormBox gridcol={"grid-cols-1"} className={"sm:pt-[0px]"}>
                      <FormController
                        control={control}
                        name={"title"}
                        label={"タイトル"}
                        type={"text"}
                        required
                      />
                      <RadioButton
                        control={control}
                        children={undefined}
                        name={"type"}
                        label={"動画タイプ"}
                        value={selectType}
                        radioButtonItem={selectTypeItems}
                        className={"pb-4"}
                        vertical
                      />
                      <FormController
                        control={control}
                        name={"url"}
                        label={"コンテンツのURL"}
                        type={"text"}
                        required
                      />
                      {optionFlg !== null && optionFlg.point && (
                        <FormBox gridcol={"grid-cols-1"} className={"!pt-[0px] !gap-2 text-sm"}>
                          {/* ポイント設定ラベルと設定ボタン */}
                          <div className="flex items-center justify-between w-full">
                            <Label id="" className="block text-sm font-medium text-gray-700">ポイント</Label>
                            {!pointSettingFlg && (
                              <Button size="sm" className={"w-[132px] ml-2 h-8 flex items-center justify-center"} onClick={openMoviePointSettingModal} children={"設定"} />
                            )}
                          </div>
                          {pointSettingFlg ? (
                            <>
                              <div>
                                <p>ポイント名　　：{getValues("pointName")}</p>
                                <p>ポイントタイプ：{getValues("pointTypeName")}</p>
                                <p>ポイント数　　：{getValues("pointAssignmentPoint")}</p>
                              </div>
                              <div className="flex justify-end gap-[10px] w-full">
                                <Button size="sm" className={"w-[132px] h-8"} onClick={openMoviePointSettingModal} children={"変更"}/>
                                <Button
                                  onClick={handlePointDelete}
                                  outline={true}
                                  className={`bg-white w-[132px] h-8`}
                                  children={'キャンセル'}
                                  size={"sm"}
                                ></Button>
                              </div>
                            </>
                          ) : null}
                          <MoviePointSettingModal
                            ref={pointModalRef}
                            open={isMoviePointSettingModalOpen}
                            toggle={closeMoviePointSettingModal}
                            closeOnClickOutside
                            pointId={getValues("pointId")}
                            onSubmit={handlePointSettingSubmit} 
                            children={undefined}                          
                          />
                          <div ref={modalContentRef} tabIndex={-1}></div>
                        </FormBox>
                      )}
                    </FormBox>
                    <Controller
                      name="explanation"
                      control={control}
                      render={({ field, fieldState }) => (
                        <TextField
                          {...field}
                          type="textarea"
                          label="説明文"
                          className="h-96 pb-10"
                          error={fieldState.error}
                          helperText={fieldState.error?.message}
                        />
                      )}
                    />
                  </FormBox>
                </div>
              </div>
            </div>
            <ModalFoterBox>
              <Button
                className="mx-2 mt-1 w-[132px]"
                outline
                onClick={toggle}
                children={"キャンセル"}
              />
              <Button
                className="mt-1 mr-2 w-[132px]"
                children={'登録'}
                submit
                disabled={isLoad}
              />
            </ModalFoterBox>
          </form>
        </Dialog.Panel>
      </Modal>
    </>
  )
}